import { Link, useNavigate } from 'react-router-dom';

import { useHome } from '@/lib/queries';
import { useCartCount } from '@/lib/cart';
import { useSector, useSectorInfo, useSectorPath, VET_STANDING } from '@/lib/sector';
import { BannerCarousel } from './BannerCarousel';
import { SponsoredBlock } from '@/components/catalogue/SponsoredBlock';
import { CategoryRail } from './CategoryRail';
import { DeliverTo } from '@/components/ui/DeliverTo';
import { useDeliveryLocation } from '@/lib/deliveryLocation';
import { Icon } from '@/components/icons/Icon';
import { AppBar, AppBarButton } from '@/components/ui/AppBar';
import { SearchField } from '@/components/ui/SearchField';
import { TrustBanner } from '@/components/ui/TrustBanner';
import { NotificationBell } from '@/components/ui/NotificationBell';
import { Card, SectionHeader } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState, DeclinedCardArt } from '@/components/ui/ErrorState';
import { Button } from '@/components/ui/Button';
import { ProductCard } from '@/features/product/ProductCard';
import type { Pharmacy } from '@chicare/shared';

/**
 * Home.
 *
 * The mockup's ordering is deliberate and preserved exactly:
 *
 *   1. search, pinned — most sessions begin with a drug name copied off a
 *      doctor's note or read off an empty pack
 *   2. delivery location, directly beneath it — price AND availability both
 *      depend on it, so it cannot be buried in settings
 *   3. categories
 *   4. the trust panel
 *   5. verified pharmacies near you
 *   6. best sellers
 *
 * ── THE SAME SCREEN SERVES BOTH SHELVES ─────────────────────────────────────
 *
 * The pharmacy and the veterinary shop are the same six sections in the same
 * order, because they are the same job: find the thing, see who has it, believe
 * the pack is real. What changes is the sector passed to the API, the wording,
 * and — on the veterinary side — a standing line that is not a banner to be
 * dismissed but part of the page.
 *
 * Two copies of this file would drift within a month, and the half that drifts
 * would be the animal-health half, because it is the one nobody demos.
 */
export function HomeScreen() {
  const navigate = useNavigate();
  const sector = useSector();
  const info = useSectorInfo();
  const path = useSectorPath();
  // The state was never passed. The header asserted a location and every
  // query behind it asked for the whole country, so "pharmacies near you" and
  // the delivery estimate were both answering a different question than the
  // one on screen.
  const { location } = useDeliveryLocation();
  const { data, isLoading, isError, refetch } = useHome(location?.state, sector);
  const cartCount = useCartCount();
  const isVet = sector === 'veterinary';

  return (
    <>
      <AppBar
        brand
        actions={
          <>
            {/* The bell, at last. The note that used to sit here said there
                was no notifications screen to send anybody to — and meanwhile
                the refund flow had been writing notifications into a table
                nobody could read since the trust tables went in. */}
            <NotificationBell />
            <AppBarButton icon="cart" label="Cart" to="/cart" dot={cartCount > 0} />
          </>
        }
      >
        <div className="mt-2">
          <SearchField
            readOnly
            onClick={() => navigate(path('/search'))}
            className="cursor-pointer"
            placeholder={info.searchPlaceholder}
          />
          {/* §11: location sits directly under search because both price and
              availability depend on it. */}
          {/* §11: location sits directly under search because both price and
              availability depend on it — which is exactly why it must not be a
              hardcoded string, as it was. */}
          {/* Location on the left, the blog on the right, on one row.
              ── WHY THE BLOG IS UP HERE AT ALL ──
              At 1280px and above the header carries it in the category bar.
              Below that the bar is gone, and the only way in was the footer —
              the bottom of a long scroll, reached by the people who had
              already given up. This row is the first thing under the search
              box, and the one line of spare width on the whole screen.

              It is a quiet control, not a button: it must not compete with
              search above it or with the shelf below it. */}
          <div className="mt-1 flex items-center justify-between gap-2">
            <DeliverTo />

            <Link
              to={path('/blog')}
              data-testid="home-blog"
              className="-mr-2 flex min-h-11 shrink-0 items-center rounded-btn px-2 text-[11.5px] font-semibold text-blue no-underline hover:bg-paper"
            >
              Blog
            </Link>
          </div>
        </div>
      </AppBar>

      <div className="mx-auto w-full max-w-[1200px] px-4 pb-6 xl:px-6 xl:pt-6">
        {isVet ? <VetHeading /> : null}

        {/* The mockup's order is preserved exactly: categories, then the
            promise, then the shelf. */}
        {data ? (
          <CategoryRail
            categories={data.categories}
            art={data.shopfront.category_art}
            drift={data.shopfront.category_scroll}
            secondsPerTile={data.shopfront.category_scroll_seconds}
          />
        ) : (
          <CategoryGridSkeleton />
        )}

        {/* Under the tiles, which is where somebody's eye already is after
            scanning the categories and finding nothing they wanted. Both
            shelves get the identical component: the only difference is which
            slides the API returned, and that is decided by the sector wall
            rather than by anything on this screen.

            The gap is deliberate and larger than the grid's own spacing. At
            mt-3.5 the carousel read as a fifth row of the category grid — two
            unrelated things touching, with nothing to say where one ended. A
            banner is a different KIND of thing from a shelf of links, and the
            space is what says so. */}
        {data ? (
          <BannerCarousel
            className="mt-7 sm:mt-8 xl:mt-10"
            rows={data.banners}
            rowCount={data.shopfront.banner_rows}
            intervalSeconds={data.shopfront.banner_interval_seconds}
            animation={data.shopfront.banner_animation}
            autoplay={data.shopfront.banner_autoplay}
            ratio={data.shopfront.banner_ratio}
          />
        ) : (
          <BannerSkeleton />
        )}

        {/* Advertisements, in their own labelled block, below the shop's own
            banners.

            ── TWO SURFACES, ON PURPOSE, NEITHER GATING THE OTHER ──

            This block and the shelf below read the SAME adverts — placement
            `home`, kind `product` — and that is the intended arrangement: a
            paid product appears here as a pack to recognise, with no price, and
            again on the shelf in its ordinary place with a price and a Featured
            mark. Two jobs, two shapes.

            What matters is that neither query EXCLUDES the other's rows. The
            block does not skip a product because the shelf has it, and
            HomeController::shelf() does not skip one because the block does.
            Either surface can be emptied, moved or re-placed without the other
            noticing, which is the whole reason they are kept apart.

            The one thing they must not do is say the same sentence twice, which
            is why the shelf's disclosure below is about POSITION and this
            block's — which the API writes, not this screen — is about presence.

            The comment that used to sit here said adverts were "never mixed
            into the shelf". That stopped being true the day the shelf started
            ordering featured products first, and a comment that is confidently
            wrong is worse than no comment at all. */}
        <SponsoredBlock placement="home" kind="product" className="mt-7 sm:mt-8" />

        <PrescriptionBanner />

        {!isVet ? <AnimalHealthMenu /> : null}

        {isVet ? <WithdrawalCard /> : null}

        {/* The promise the whole architecture exists to make true.
            Rendered OUTSIDE the loading and error branches on purpose: it is
            true whether or not the shelf loaded, and a buyer staring at an
            error is exactly the person who most needs to know ChiCare stands
            behind what it sells. */}
        <TrustBanner
          className="mt-3.5"
          headline={data?.trust.headline}
          sub={data?.trust.sub}
          verifiedPacks={data?.trust.verified_packs_this_month ?? 0}
          counterfeits={data?.trust.counterfeits_found_this_month ?? 0}
        />

        {isError ? (
          <ErrorState
            illustration={<DeclinedCardArt />}
            title="We couldn't load the shelf"
            body="This is usually a connection problem rather than something wrong with your account."
            action={
              <Button full icon="refresh" onClick={() => refetch()}>
                Try again
              </Button>
            }
            footnote="Nothing you had saved is lost."
          />
        ) : isLoading || !data ? (
          <HomeSkeleton />
        ) : (
          <>
            <section className="mt-5">
              <SectionHeader
                title={isVet ? 'Verified animal-health sellers near you' : 'Verified pharmacies near you'}
                action={
                  <Link to={path('/search')} className="-my-2 inline-flex min-h-11 items-center px-1 text-[11.5px] font-medium text-blue no-underline">
                    See all
                  </Link>
                }
              />
              <div className="grid gap-2.5 md:grid-cols-2 xl:grid-cols-4">
                {data.pharmacies.slice(0, 4).map((pharmacy) => (
                  <PharmacyCard key={pharmacy.id} pharmacy={pharmacy} />
                ))}
              </div>
            </section>

            {/* ────────────────── THE SHELF ──────────────────
                Twenty products, which on a phone is two across and ten rows
                deep. That depth is the point: it is the only part of the home
                screen where somebody can browse rather than search, and four
                cards was a sample rather than a shop.

                ── WHAT ORDERS IT ──

                Featured first — products a seller paid to show here — then the
                newest stock to fill the twenty. The fallback is what makes it
                work before ChiCare has any advertisers: on a day nobody has
                bought a placement the shelf is simply the newest stock, and
                nothing on the screen looks empty or broken.

                ── AND WHAT THE HEADING SAYS ──

                It changes with the content, because "Featured" over a shelf
                nobody paid for is a lie, and a heading that is measurably
                false is worse than no heading. That is what replaced "Best
                sellers this week": it was ordered by how many pharmacies
                happen to stock a thing, which is not best-selling, and it
                counted since the beginning of time rather than this week. */}
            <section className="mt-6">
              <SectionHeader
                title={data.shelf_featured_count > 0 ? 'Featured' : 'New on ChiCare'}
                action={
                  <Link
                    to={path('/search')}
                    data-testid="shelf-view-all"
                    className="-my-2 inline-flex min-h-11 items-center px-1 text-[11.5px] font-medium text-blue no-underline"
                  >
                    View all
                  </Link>
                }
              />
              <div
                data-testid="home-shelf"
                className="grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-5"
              >
                {/* `?? best_sellers` so a cached payload from the release
                    before this one still renders a shelf instead of nothing. */}
                {(data.shelf ?? data.best_sellers).map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>

              {/* Said once, under the shelf, rather than on twenty cards. The
                  per-card mark says WHICH were paid for; this says what that
                  bought, which is the part a buyer cannot infer from a badge.

                  Deliberately about POSITION and about nothing else. The
                  Sponsored block above carries its own disclosure, written by
                  the API, and it is about presence — "these products were paid
                  to appear here". Two sentences a paragraph apart both saying
                  "paid to appear" reads as a stutter, and a buyer who skims a
                  repeated line stops reading the one that matters. */}
              {data.shelf_featured_count > 0 ? (
                <p className="m-0 mt-3 text-[11px] leading-relaxed text-faint">
                  Paid placement decides the ORDER of this shelf, never the prices. Every figure
                  above is the lowest offer for that pack.
                </p>
              ) : null}
            </section>
          </>
        )}
      </div>
    </>
  );
}

/**
 * The way in for somebody holding a piece of paper.
 *
 * Directly under the categories, because it is the answer for the buyer the
 * category grid cannot serve: half of what is on a prescription is not
 * browsable, and the rest is written in a hand they cannot read. Searching is
 * the wrong tool for them and a shelf is the wrong shape.
 */
function PrescriptionBanner() {
  const path = useSectorPath();
  const sector = useSector();
  const isVet = sector === 'veterinary';

  return (
    <Link
      to={path('/prescriptions')}
      data-testid="prescription-banner"
      // The same gradient the trust banner wears, for the same reason: this is
      // not a shelf item, it is a different WAY IN — for the buyer the category
      // grid cannot serve, holding a piece of paper half of which is not
      // browsable and the rest written in a hand they cannot read.
      //
      // As a white card it sat between two white cards and read as one more
      // row. The colour is what says "this is a different kind of thing".
      className="mt-4 flex items-center gap-3 rounded-card bg-[linear-gradient(135deg,var(--color-blue-deep),var(--color-blue))] px-4 py-3.5 text-white no-underline"
    >
      {/* The tile and the marks all move to white-on-blue. A blue icon on
          a blue-tint square was invisible the moment the card stopped
          being white, and "it still passes contrast somewhere else" is
          not an argument for leaving it. */}
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px] bg-white/15">
        <Icon name="doc" size={19} strokeWidth={1.9} className="text-white" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-bold text-white">
          {isVet ? 'Have a vet prescription?' : 'Have a prescription?'}
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-white/75">
          Upload a photo and {isVet ? 'sellers' : 'pharmacies'} near you reply with a price. You only
          pay if you accept one.
        </span>
      </span>

      <Icon name="chevron" size={16} strokeWidth={2} className="shrink-0 text-white/80" />
    </Link>
  );
}

/**
 * The way into animal health, from the human home screen.
 *
 * ── WHY IT IS A NAMED MENU AND NOT A CATEGORY TILE ──────────────────────────
 *
 * A seventh tile in the category grid reading "Veterinary" would say that
 * animal health is a shelf inside this shop. It is not — it is a different shop
 * with a different licence behind every seller and a different set of rules on
 * every pack, and somebody who taps through to it needs to know they have gone
 * somewhere, not just filtered something.
 *
 * ── AND WHY THERE ARE TWO ENTRIES, NOT ONE ──────────────────────────────────
 *
 * Medicines and equipment are separate purchases by separate people on separate
 * days: the farmer buying Newcastle vaccine is not the one buying a drenching
 * gun. One combined "Animal health" link would bury the second of those behind
 * a search nobody runs.
 *
 * It stays even though the switch above the header goes to the same place,
 * because it does something the switch cannot: it names the two shelves. "Take
 * me to the veterinary shop" is rarely the actual want — dewormers are, or a
 * drenching gun — and this puts each of those one tap away instead of two.
 */
function AnimalHealthMenu() {
  const entries = [
    ['Veterinary medicines', '/vet/search?category=vet-medicines', 'Dewormers, vaccines, antibiotics and pet care.'],
    ['Veterinary equipment', '/vet/search?category=vet-equipment', 'Dosing guns, drinkers, clinic and farm instruments.'],
  ] as const;

  return (
    <section
      data-testid="animal-health-menu"
      aria-label="Veterinary"
      className="mt-4 overflow-hidden rounded-card border border-line bg-white"
    >
      <Link to="/vet" className="flex items-center gap-3 border-b border-line px-4 py-3 no-underline">
        <span className="min-w-0 flex-1">
          <span className="block font-mono text-[10px] uppercase tracking-[0.14em] text-blue">
            Also on ChiCare
          </span>
          <span className="mt-0.5 block font-display text-[16px] font-bold tracking-[-0.015em] text-ink">
            Veterinary
          </span>
          <span className="mt-0.5 block text-[12px] leading-snug text-muted">
            For livestock, poultry and pets. Every seller VCN-registered.
          </span>
        </span>
        <Icon name="chevron" size={16} strokeWidth={2} className="shrink-0 text-blue" />
      </Link>

      {entries.map(([label, to, blurb]) => (
        <Link
          key={to}
          to={to}
          className="flex min-h-11 items-center gap-3 border-b border-line-2 px-4 py-2.5 no-underline last:border-b-0"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[13px] font-semibold text-ink">{label}</span>
            <span className="block text-[11.5px] leading-snug text-faint">{blurb}</span>
          </span>
          <Icon name="chevron" size={14} strokeWidth={2} className="shrink-0 text-faint" />
        </Link>
      ))}
    </section>
  );
}

/**
 * "Every pack carries its withdrawal period."
 *
 * ── WHAT WAS HERE BEFORE, AND WHY IT WENT ────────────────────────────
 *
 * The seller pitch — "Sell veterinary on ChiCare", a tinted card about VCN
 * registration and bank settlement. The reasoning for putting it here was that
 * an agrovet store browsing the animal-health shelf is the likeliest seller
 * ChiCare will ever have. That is true, and it is still the wrong place: for
 * every one of them there are a hundred people who came to buy a dewormer, and
 * all hundred got an advert in the second row of the shop.
 *
 * The pitch is not gone. It is on the account screen, where somebody has gone
 * looking for it, and the full write-up behind it now carries both application
 * links. See PartnerRows in AccountScreen, and ApplyBlock in PageScreen.
 *
 * ── AND WHY THIS REPLACED IT ─────────────────────────────────────
 *
 * A withdrawal period is the time that must pass between the last dose an
 * animal is given and the day its milk, eggs or meat may be sold. It is the one
 * fact that is true of animal medicine and has no counterpart in human
 * medicine, and it is the one a farmer gets wrong at somebody else's expense:
 * by reading the period for the wrong species, by counting from the first dose
 * instead of the last, or by treating one cow and selling from the whole tank.
 *
 * Getting it wrong puts drug residue in food. That is a bigger thing than
 * anything else competing for this row.
 *
 * ── AND WHY IT LINKS RATHER THAN EXPLAINS ────────────────────────────
 *
 * Three paragraphs of dosing guidance on a home screen is three paragraphs
 * nobody reads, and half-remembered dosing guidance is worse than none. One
 * sentence and a way through to the article is read by the people it is for.
 *
 * The link is sector-aware, so it lands on /vet/blog/... and the reader stays
 * on the animal-health side of the wall they were already on.
 */
function WithdrawalCard() {
  const path = useSectorPath();

  return (
    <Link
      to={path('/blog/withdrawal-periods-what-they-mean')}
      data-testid="withdrawal-card"
      className="mt-3.5 flex items-center gap-3 rounded-card border border-line bg-white px-4 py-3.5 no-underline"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[11px] bg-blue-tint">
        <Icon name="clock" size={19} strokeWidth={1.9} className="text-blue" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block text-[13.5px] font-bold text-ink">
          Every pack carries its withdrawal period
        </span>
        <span className="mt-0.5 block text-[12px] leading-snug text-muted">
          The days between the last dose and the day milk, eggs or meat can be sold. Read how to
          count them.
        </span>
      </span>

      <Icon name="chevron" size={16} strokeWidth={2} className="shrink-0 text-blue" />
    </Link>
  );
}

/**
 * The line at the top of the animal-health shelf.
 *
 * Part of the page, not a banner. A dismissible notice is one people dismiss,
 * and the whole point is that it is still there on the twentieth visit when
 * somebody is buying a dewormer in a hurry and has stopped reading carefully.
 */
function VetHeading() {
  return (
    <section className="border-b border-line pb-3.5 pt-3.5">
      {/* No "CHICARE VET" eyebrow. The wordmark in the header already reads
          "ChiCare VET" and the switch beside it is set to the paw — three
          statements of the same fact within one screen height. */}
      <h1 className="m-0 font-display text-[21px] font-bold leading-tight tracking-[-0.02em]">
        Veterinary
      </h1>

      {/* One line, and it is the SAFETY one.
          The sentence that went was "Every seller here is registered with the
          Veterinary Council of Nigeria, and every pack carries its withdrawal
          period." Both facts are true and both are already shown where they can
          be acted on: the licence on the seller, the withdrawal period in a
          table on the pack. Here they were two more clauses in front of the one
          clause that stops somebody dosing a child with a product bought for a
          goat. */}
      <p className="m-0 mt-1 text-[13px] font-medium leading-relaxed text-ink">
        {VET_STANDING}
      </p>
    </section>
  );
}

function PharmacyCard({ pharmacy }: { pharmacy: Pharmacy }) {
  return (
    <Card className="!p-[11px]">
      <div className="flex items-center gap-2.5">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[10px] bg-blue-tint font-display text-[13px] font-bold text-blue">
          {pharmacy.initials}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <b className="truncate text-[13px]">{pharmacy.name}</b>
            {/* No verified mark. ChiCare lists only PCN-licensed pharmacies,
                so this was true of every row and marked all of them
                identically — which is decoration, not information. The header
                says it once for the whole list. */}
          </div>
          <div className="flex items-center gap-1.5 text-[11px] text-faint">
            <span className="truncate">{pharmacy.lga}</span>
            <span className="tracking-[0.5px] text-caution">★ {pharmacy.quality_score.toFixed(1)}</span>
          </div>
        </div>
        <Badge tone="verified">Open</Badge>
      </div>
    </Card>
  );
}

/** §11: loading is a skeleton, never a spinner on a list. */
/**
 * Two grey strips at the carousel's exact aspect ratio.
 *
 * Sized rather than guessed, so the shelf below does not jump down the page
 * when the real slides arrive. A layout shift on the first screen a buyer ever
 * sees is the shop front dropping what they were about to tap.
 */
function BannerSkeleton() {
  return (
    <div className="mt-7 space-y-2.5 sm:mt-8 xl:mt-10" aria-hidden>
      {Array.from({ length: 2 }).map((_, index) => (
        // The DEFAULT shape, not the tallest. The configured ratio arrives
        // with the payload, so the skeleton has to guess — and guessing 16:7
        // over-reserves on a desktop, which makes the whole shelf jump
        // UPWARD when the real 5:1 strip replaces it. Guessing the default is
        // right for everybody who has not changed it, and an admin who has
        // sees one small settle on a cold load.
        <Skeleton key={index} className="aspect-[16/7] w-full sm:aspect-[5/1]" radius="card" />
      ))}
    </div>
  );
}

function CategoryGridSkeleton() {
  return (
    // One row, matching the rail. A grid here and a rail a moment later means
    // the whole shelf below jumps the instant the categories arrive.
    <div className="no-scrollbar flex gap-2 overflow-hidden pt-3.5">
      {Array.from({ length: 8 }).map((_, index) => (
        <div key={index} className="w-[88px] shrink-0 sm:w-[104px] xl:w-[120px]">
          <Skeleton className="aspect-square w-full" radius="md" />
          <Skeleton className="mt-1.5 h-2" />
        </div>
      ))}
    </div>
  );
}

function HomeSkeleton() {
  return (
    <div>
      <Skeleton className="mt-5 h-3 w-[45%]" />
      <div className="mt-2.5 grid gap-2.5 md:grid-cols-2 xl:grid-cols-4">
        {Array.from({ length: 4 }).map((_, index) => (
          <Skeleton key={index} className="h-[62px] w-full" radius="card" />
        ))}
      </div>

      <Skeleton className="mt-6 h-3 w-[40%]" />
      <div className="mt-2.5 grid grid-cols-2 gap-2.5 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, index) => (
          <Skeleton key={index} className="h-[210px] w-full" radius="card" />
        ))}
      </div>
    </div>
  );
}
